import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import log from 'loglevel';
import { Observable } from 'rxjs';

import type { UseAuthResult } from '../../hooks/useAuth';

type ErrorLinkAuth = Pick<UseAuthResult, 'getAccessTokenSilently' | 'logout'>;

// Error link - handles tenant-specific errors with retry logic.
//
// A retry only happens if the Observable is returned from the onError callback
// itself. That is why the GraphQL errors are walked with for...of rather than
// forEach: a value returned from a forEach callback is discarded, which used
// to mean every retry below was built and never subscribed, so the original
// error reached the query unchanged. Branches that don't retry `continue` to
// the next error, as they did before.
export function createErrorLink({ getAccessTokenSilently, logout }: ErrorLinkAuth): ErrorLink {
  return new ErrorLink(({ error, operation, forward }) => {
    if (CombinedGraphQLErrors.is(error)) {
      for (const { message } of error.errors) {
        // Handle tenant context required error with retry logic
        if (message.includes('Tenant context is required') || message.includes('tenant context')) {
          log.warn('Tenant context required - attempting to restore context');

          const storedTenantId = localStorage.getItem('selectedTenantId');
          if (storedTenantId) {
            // Try to restore tenant context by re-sending the select tenant mutation
            // This is a fallback mechanism to handle temporary context loss
            log.info('Attempting to restore tenant context for:', storedTenantId);

            // Set a flag to prevent infinite retries
            const retryCount = operation.getContext().retryCount || 0;
            if (retryCount < 2) {
              operation.setContext({
                ...operation.getContext(),
                retryCount: retryCount + 1,
                headers: {
                  ...operation.getContext().headers,
                  'X-Tenant-ID': storedTenantId,
                },
              });

              // Retry the operation with tenant header
              return forward(operation);
            }
          }

          // If retry failed or no stored tenant, redirect to tenant selection
          log.error('Failed to restore tenant context - redirecting to tenant selection');
          localStorage.removeItem('selectedTenantId');
          sessionStorage.removeItem('selectedTenantId');
          window.location.href = '/select-tenant';
          continue;
        }

        // Handle no access error
        if (
          message.includes('You do not have access to this tenant') ||
          (message.includes('access') && message.includes('tenant'))
        ) {
          log.error('Access denied to tenant - clearing cache and logging out');
          // Clear ALL auth state to prevent stuck sessions
          localStorage.clear();
          sessionStorage.clear();
          logout({ returnTo: window.location.origin });
          continue;
        }

        // Handle general authorization errors — try a token refresh + retry first.
        // Only log the user out if the refresh itself fails.
        if (message.includes('Unauthorized') || message.includes('Token has expired')) {
          const retryCount = operation.getContext().retryCount || 0;
          if (retryCount < 1) {
            log.warn('GraphQL Unauthorized — refreshing token and retrying');
            return new Observable((observer) => {
              getAccessTokenSilently()
                .then((token) => {
                  operation.setContext({
                    retryCount: retryCount + 1,
                    headers: {
                      ...operation.getContext().headers,
                      authorization: `Bearer ${token}`,
                    },
                  });
                  forward(operation).subscribe(observer);
                })
                .catch((err) => {
                  log.error('Token refresh failed after GraphQL Unauthorized:', err);
                  localStorage.clear();
                  sessionStorage.clear();
                  logout({ returnTo: window.location.origin });
                  observer.error(err);
                });
            });
          }

          log.error('Authorization error after retry — logging out');
          localStorage.clear();
          sessionStorage.clear();
          logout({ returnTo: window.location.origin });
          continue;
        }

        if (message.includes('Forbidden')) {
          log.error('Forbidden — clearing cache and logging out');
          localStorage.clear();
          sessionStorage.clear();
          logout({ returnTo: window.location.origin });
          continue;
        }

        console.error(`[GraphQL error]: ${message}`);
      }
    }

    // Anything that is not a GraphQL error is a transport failure.
    if (!CombinedGraphQLErrors.is(error)) {
      log.error(`[Network error]: ${error}`);

      // HTTP 401 — try refreshing the token and retrying the operation once.
      if (ServerError.is(error) && error.statusCode === 401) {
        const retryCount = operation.getContext().retryCount || 0;
        if (retryCount < 1) {
          log.warn('HTTP 401 — refreshing token and retrying');
          return new Observable((observer) => {
            getAccessTokenSilently()
              .then((token) => {
                operation.setContext({
                  retryCount: retryCount + 1,
                  headers: {
                    ...operation.getContext().headers,
                    authorization: `Bearer ${token}`,
                  },
                });
                forward(operation).subscribe(observer);
              })
              .catch((err) => {
                log.error('Token refresh failed after HTTP 401:', err);
                localStorage.clear();
                sessionStorage.clear();
                logout({ returnTo: window.location.origin });
                observer.error(err);
              });
          });
        }

        log.error('HTTP 401 after retry — logging out');
        localStorage.clear();
        sessionStorage.clear();
        logout({ returnTo: window.location.origin });
        return undefined;
      }

      // HTTP 403 is a permissions error, not an expired token — log out immediately.
      if (ServerError.is(error) && error.statusCode === 403) {
        log.error('HTTP 403 — clearing cache and logging out');
        localStorage.clear();
        sessionStorage.clear();
        logout({ returnTo: window.location.origin });
        return undefined;
      }
    }
    return undefined;
  });
}
